import type { Setting, Texts, Shipment } from "@/type";
import { formatAddressLines, formatTimestamp, hasWarehouseAddress } from "@/utils/tracking";

interface ShippingInfoCardProps {
  settings: Setting;
  texts: Texts;
  shipment: Shipment;
}

export default function ShippingInfoCard({ settings, texts, shipment }: ShippingInfoCardProps) {
  const { style, font_family } = settings;
  const { colors, general_content_text_size, borders } = style;
  const info = shipment.shipping_info;
  const showWarehouse = hasWarehouseAddress(info.warehouse_address);
  const warehouseLines = formatAddressLines(info.warehouse_address);

  const titleStyle = {
    color: colors.text_color.information_title_text,
    fontSize: general_content_text_size.information_title_text,
    fontWeight: 650,
    lineHeight: "20px",
    marginBottom: 16,
  };

  const infoStyle = {
    color: colors.text_color.information_text,
    fontSize: general_content_text_size.information_text,
    fontWeight: 450,
    lineHeight: "20px",
  };

  const labelStyle = {
    ...infoStyle,
    fontWeight: 550,
    color: colors.text_color.information_title_text,
  };

  const rows: { label: string; value: string; lines?: string[] }[] = [
    { label: texts.order, value: info.order || "" },
    { label: texts.fulfillment_time, value: formatTimestamp(info.fulfillment_time) },
    ...(info.tracking_number?.trim()
      ? [{ label: texts.tracking_number, value: info.tracking_number }]
      : []),
    { label: texts.carrier, value: info.carrier || "" },
    { label: texts.shipping_method, value: info.shipping_method || "" },
  ];

  if (showWarehouse) {
    rows.push({
      label: texts.warehouse_address,
      value: "",
      lines: warehouseLines,
    });
  }

  return (
    <div
      className="overflow-clip"
      style={{
        backgroundColor: colors.general_color.information_card_background,
        borderRadius: borders.information_card_corner_radius,
        fontFamily: font_family.font,
        border: "1px solid #e3e3e3",
        padding: 16,
      }}
    >
      <p style={titleStyle}>{texts.shipping_information}</p>
      <div className="flex flex-col" style={{ gap: 16 }}>
        {rows.map((row) => (
          <div key={row.label} className="flex flex-col" style={{ gap: 4 }}>
            <p style={labelStyle}>{row.label}</p>
            {row.lines && row.lines.length > 0 ? (
              <div style={infoStyle}>
                {row.lines.map((line, i) => (
                  <p key={i} style={{ margin: 0 }}>
                    {line}
                  </p>
                ))}
              </div>
            ) : (
              <p style={infoStyle}>{row.value || "—"}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
